/**
 * Eric BYOK (2026-05-12) — Wizard onboarding 1er login admin.
 *
 * Affiché UNE SEULE FOIS au premier login d'un admin si son Forfait AI
 * (clés OpenAI/AssemblyAI/OVH) n'est pas complet. Force la config avant
 * tout traitement vidéo, pour éviter les crashs pipeline "key not configured".
 *
 * - Trigger : admin connecté + status.complete === false + jamais fermé manuellement.
 * - Persistance : localStorage `knoria_byok_wizard_seen_<email>` (peut être réouvert manuellement).
 * - 4 étapes : Bienvenue → OpenAI → AssemblyAI → OVHcloud → Terminé.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { canAccessAdmin } from "../utils/permissions";
import { toast } from "./ui/sonner";
import { confirmDialog } from "../lib/uiDialogs";

import { API_BASE as API } from "../lib/apiBase";

const STEPS = [
  { id: "welcome", title_fr: "Bienvenue", title_en: "Welcome" },
  { id: "openai", title_fr: "OpenAI (GPT-5.2 + Whisper)", title_en: "OpenAI (GPT-5.2 + Whisper)" },
  { id: "assemblyai", title_fr: "AssemblyAI", title_en: "AssemblyAI" },
  { id: "ovh_storage", title_fr: "Stockage OVHcloud (Québec)", title_en: "OVHcloud Storage (Québec)" },
  { id: "done", title_fr: "Tout est prêt", title_en: "All set" },
];

const SERVICE_INFO = {
  openai: {
    icon: "🤖",
    signup: "https://platform.openai.com/api-keys",
    desc_fr: "Génère les transcriptions (Whisper) et toute la connaissance (Q&R, procédures, scripts) via GPT-5.2.",
    desc_en: "Powers transcripts (Whisper) and all knowledge extraction (Q&A, procedures, scripts) via GPT-5.2.",
    cost: "20-150 CAD$ / mois",
    placeholder: "sk-proj-...",
  },
  assemblyai: {
    icon: "🎙️",
    signup: "https://www.assemblyai.com/dashboard/signup",
    desc_fr: "Identifie automatiquement les locuteurs (diarisation) dans vos vidéos.",
    desc_en: "Automatically identifies speakers (diarisation) in your videos.",
    cost: "15-80 CAD$ / mois",
    placeholder: "votre_cle_assemblyai_64car",
  },
  ovh_storage: {
    icon: "🍁",
    signup: "https://www.ovhcloud.com/fr-ca/public-cloud/object-storage/",
    desc_fr: "Stocke vos vidéos en région BHS (Beauharnois, Québec) pour la conformité Loi 25.",
    desc_en: "Stores your videos in BHS (Beauharnois, Québec) for Loi 25 compliance.",
    cost: "5-30 CAD$ / mois",
    placeholder: "OVH Access Key",
  },
};

function StepBubble({ index, current, completed }) {
  let cls = "bg-zinc-200 text-zinc-500";
  if (completed) cls = "bg-teal-500 text-white";
  else if (index === current) cls = "bg-[#002FA7] text-white ring-4 ring-blue-100";
  return (
    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${cls}`}>
      {completed ? "✓" : index + 1}
    </div>
  );
}

export default function AIPackageOnboardingWizard() {
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    openai: { key: "" },
    assemblyai: { key: "" },
    ovh_storage: { key: "", secret_key: "", bucket_name: "", region: "bhs", endpoint: "https://s3.bhs.io.cloud.ovh.net" },
  });

  const seenKey = user?.email ? `knoria_byok_wizard_seen_${user.email}` : null;

  // ─── Détection auto au premier login admin sans Forfait AI complet ────
  // Eric (2026-06-14 — iter101) — Le wizard NE S'OUVRE PAS si :
  //   1. localStorage flag (compat existante)
  //   2. user.byok_wizard_dismissed === true (persisté server-side via
  //      POST /api/org/ai-package/wizard/dismiss → survit aux clear cache,
  //      aux changements de navigateur, etc.)
  //   3. status.force_knoria_keys === true (Knoria fournit déjà les 3 clés)
  //   4. status.complete === true (les 3 clés sont déjà configurées)
  useEffect(() => {
    if (!user || !user.email) return;
    // Eric 2026-06-25 (iter118 B3) — migré vers canAccessAdmin.
    if (!canAccessAdmin(user)) return;
    if (location.pathname.startsWith("/dashboard/settings/ai-package")) return;
    if (location.pathname === "/dashboard/onboarding/forfait-ai") return;
    if (user.byok_wizard_dismissed === true) return;
    const seen = seenKey ? localStorage.getItem(seenKey) : "1";
    if (seen) return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/org/ai-package/status`, { withCredentials: true });
        if (cancelled) return;
        setStatus(data);
        if (data.complete || data.force_knoria_keys) {
          // Tout est déjà configuré (ou l'org tourne sur les clés Knoria)
          // → on marque comme vu silencieusement, sans afficher la modale.
          if (seenKey) localStorage.setItem(seenKey, "1");
          return;
        }
        setOpen(true);
      } catch {
        // Silent fail — on ne bloque pas la navigation pour un wizard
      }
    })();
    return () => { cancelled = true; };
  }, [user, location.pathname, seenKey]);

  // Refresh status quand le wizard est ouvert (post-save)
  const refreshStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/org/ai-package/status`, { withCredentials: true });
      setStatus(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  const close = async (skipConfirm = false) => {
    // Vincent feedback 2026-02 — Skip confirm sur l'étape welcome (l'user n'a
    // encore rien rempli, donc pas de risque de perdre du travail).
    const isWelcomeStep = STEPS[stepIdx]?.id === "welcome";
    if (!skipConfirm && !isWelcomeStep && status && !status.complete) {
      const ok = await confirmDialog(
        lang === "fr"
          ? "Le Forfait AI n'est pas complet — certaines fonctionnalités (transcription, Q&R, etc.) seront désactivées tant que les 3 clés ne sont pas configurées. Quitter quand même ?"
          : "AI Package is not complete — some features (transcription, Q&A, etc.) will be disabled until all 3 keys are configured. Exit anyway?"
      );
      if (!ok) return;
    }
    if (seenKey) localStorage.setItem(seenKey, "1");
    setOpen(false);
  };

  // Eric (2026-06-14 — iter101) — Skip permanent : marque dans la base que
  // l'utilisateur a définitivement choisi de ne plus voir ce wizard (utile
  // pour les orgs qui tournent sur Knoria keys ou qui configurent leur BYOK
  // via la page Forfait AI directement sans passer par le wizard).
  const dismissPermanently = async () => {
    const ok = await confirmDialog(
      lang === "fr"
        ? "Ne plus jamais afficher ce wizard ?\n\nVous pourrez toujours configurer vos clés depuis Réglages → Forfait AI. Cette préférence est enregistrée sur votre compte."
        : "Never show this wizard again?\n\nYou can still configure your keys from Settings → AI Package. This preference is saved on your account."
    );
    if (!ok) return;
    try {
      await axios.post(`${API}/api/org/ai-package/wizard/dismiss`, {}, { withCredentials: true });
      if (seenKey) localStorage.setItem(seenKey, "1");
      toast.success(lang === "fr" ? "Préférence enregistrée." : "Preference saved.");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "fr" ? "Erreur sauvegarde" : "Save error"));
    }
  };

  const saveCurrentService = async () => {
    const service = STEPS[stepIdx].id;
    if (service === "welcome" || service === "done") return true;
    const f = form[service];
    if (!f.key || f.key.length < 8) {
      toast.error(lang === "fr" ? "Clé invalide (min 8 caractères)" : "Invalid key (min 8 chars)");
      return false;
    }
    setSaving(true);
    try {
      const payload = { key: f.key.trim() };
      if (service === "ovh_storage") {
        if (!f.secret_key) { toast.error(lang === "fr" ? "Secret Access Key requis" : "Secret Access Key required"); setSaving(false); return false; }
        if (!f.bucket_name) { toast.error(lang === "fr" ? "Nom du bucket requis" : "Bucket name required"); setSaving(false); return false; }
        payload.secret_key = f.secret_key.trim();
        payload.bucket_name = f.bucket_name.trim();
        payload.region = f.region || "bhs";
        payload.endpoint = f.endpoint || "https://s3.bhs.io.cloud.ovh.net";
      }
      await axios.post(`${API}/api/org/ai-package/${service}`, payload, { withCredentials: true });
      toast.success(lang === "fr" ? "Clé validée et chiffrée ✓" : "Key validated and encrypted ✓");
      await refreshStatus();
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(typeof detail === "string" ? detail : JSON.stringify(detail), { duration: 10000 });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const skipStep = () => {
    if (stepIdx < STEPS.length - 1) setStepIdx(stepIdx + 1);
  };

  const nextStep = async () => {
    const current = STEPS[stepIdx];
    if (current.id === "welcome") { setStepIdx(1); return; }
    if (current.id === "done") { close(true); return; }
    // Si la clé est déjà configurée selon le status, on peut skip sans saving
    const svcStatus = (status?.services || []).find((s) => s.service === current.id);
    if (svcStatus?.configured && !form[current.id]?.key) {
      setStepIdx(stepIdx + 1);
      return;
    }
    const ok = await saveCurrentService();
    if (ok) setStepIdx(stepIdx + 1);
  };

  const goToFullSettings = () => {
    if (seenKey) localStorage.setItem(seenKey, "1");
    setOpen(false);
    navigate("/dashboard/settings/ai-package");
  };

  if (!open) return null;

  const step = STEPS[stepIdx];
  const isService = step.id !== "welcome" && step.id !== "done";
  const svcInfo = isService ? SERVICE_INFO[step.id] : null;
  const svcStatus = isService ? (status?.services || []).find((s) => s.service === step.id) : null;
  const isLast = stepIdx === STEPS.length - 1;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="byok-onboarding-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        data-testid="byok-onboarding-modal"
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-zinc-100 flex items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-blue-600 font-bold">
              {lang === "fr" ? "Configuration Forfait AI" : "AI Package Setup"}
            </div>
            <h2 className="text-xl font-bold text-zinc-900 mt-1">
              {lang === "fr" ? step.title_fr : step.title_en}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={dismissPermanently}
              className="text-[11px] text-zinc-500 hover:text-rose-600 underline underline-offset-2"
              data-testid="byok-onboarding-dismiss-forever"
              title={lang === "fr" ? "Ne plus jamais afficher ce wizard" : "Never show again"}
            >
              {lang === "fr" ? "Ne plus afficher" : "Don't show again"}
            </button>
            <button
              onClick={() => close(false)}
              className="text-zinc-400 hover:text-zinc-700 text-2xl leading-none"
              aria-label="Close"
              data-testid="byok-onboarding-close"
            >
              ×
            </button>
          </div>
        </div>

        {/* Stepper */}
        <div className="px-6 pt-4 flex items-center gap-2">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2 flex-1">
              <StepBubble index={i} current={stepIdx} completed={i < stepIdx} />
              {i < STEPS.length - 1 && (
                <div className={`h-0.5 flex-1 ${i < stepIdx ? "bg-teal-500" : "bg-zinc-200"}`} />
              )}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {step.id === "welcome" && (
            <div className="space-y-4" data-testid="byok-step-welcome">
              <div className="text-5xl">📦</div>
              <h3 className="text-lg font-semibold text-zinc-900">
                {lang === "fr"
                  ? "Bienvenue dans Knoria — connectez votre Forfait AI"
                  : "Welcome to Knoria — connect your AI Package"}
              </h3>
              <p className="text-sm text-zinc-700 leading-relaxed">
                {lang === "fr"
                  ? "Knoria fonctionne en mode BYOK (Bring Your Own Key) : vous payez directement OpenAI, AssemblyAI et OVHcloud — Knoria ne prélève aucune commission sur votre consommation IA."
                  : "Knoria runs in BYOK mode (Bring Your Own Key): you pay OpenAI, AssemblyAI and OVHcloud directly — Knoria takes no cut on your AI consumption."}
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex gap-2">
                  <span>🔐</span>
                  <span>{lang === "fr" ? "Vos clés sont chiffrées AES-256 au repos (conforme Loi 25)." : "Your keys are AES-256 encrypted at rest (Loi 25 compliant)."}</span>
                </li>
                <li className="flex gap-2">
                  <span>🍁</span>
                  <span>{lang === "fr" ? "Toutes les données restent au Canada (Beauharnois, Québec)." : "All data stays in Canada (Beauharnois, Québec)."}</span>
                </li>
                <li className="flex gap-2">
                  <span>⏱️</span>
                  <span>{lang === "fr" ? "Cette configuration prend environ 5 minutes." : "This setup takes about 5 minutes."}</span>
                </li>
              </ul>
              {status && (
                <div className="mt-3 px-3 py-2 rounded bg-blue-50 border border-blue-200 text-xs text-blue-900">
                  {lang === "fr"
                    ? `Statut actuel : ${status.configured_count}/${status.total_count} services configurés`
                    : `Current status: ${status.configured_count}/${status.total_count} services configured`}
                </div>
              )}
              <button
                onClick={() => { if (seenKey) localStorage.setItem(seenKey, "1"); setOpen(false); navigate("/dashboard/settings/ai-package/guide"); }}
                className="mt-2 text-xs text-amber-800 hover:text-amber-900 underline font-medium"
                data-testid="byok-onboarding-guide-link"
              >
                📘 {lang === "fr" ? "Voir le guide pas-à-pas (recommandé pour la 1re fois)" : "Open the step-by-step guide (recommended on first run)"}
              </button>
              {/* Vincent feedback 2026-02 — Bouton explicite "Ne plus afficher".
                  Eric (2026-06-14 — iter101) — Unifié pour pointer sur le
                  dismiss server-side persistant (au lieu d'un localStorage-only)
                  + testid renommé pour éviter le doublon avec le bouton header. */}
              <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-between gap-2">
                <button
                  onClick={dismissPermanently}
                  className="text-xs text-zinc-500 hover:text-rose-600 underline-offset-2 hover:underline"
                  data-testid="byok-onboarding-dismiss-welcome"
                >
                  {lang === "fr" ? "Ne plus afficher ce rappel" : "Don't show again"}
                </button>
                <span className="text-[10px] text-zinc-400">
                  {lang === "fr" ? "Vous pouvez toujours configurer depuis Réglages → Forfait AI" : "You can always configure from Settings → AI Package"}
                </span>
              </div>
            </div>
          )}

          {isService && svcInfo && (
            <div className="space-y-4" data-testid={`byok-step-${step.id}`}>
              <div className="flex items-start gap-4">
                <div className="text-4xl">{svcInfo.icon}</div>
                <div className="flex-1">
                  <p className="text-sm text-zinc-700">
                    {lang === "fr" ? svcInfo.desc_fr : svcInfo.desc_en}
                  </p>
                  <div className="mt-2 flex gap-3 text-xs">
                    <a
                      href={svcInfo.signup}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                      data-testid={`byok-step-${step.id}-signup`}
                    >
                      🔗 {lang === "fr" ? "Créer / récupérer ma clé" : "Create / retrieve my key"}
                    </a>
                    <span className="text-zinc-500">💰 {svcInfo.cost}</span>
                  </div>
                </div>
              </div>

              {svcStatus?.configured && (
                <div className="px-3 py-2 rounded bg-teal-50 border border-teal-200 text-xs text-teal-800">
                  ✓ {lang === "fr" ? "Déjà configuré" : "Already configured"} ({svcStatus.key_masked}). {lang === "fr" ? "Vous pouvez passer ou écraser." : "You can skip or overwrite."}
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-zinc-700 mb-1">
                  {step.id === "ovh_storage" ? "OVH Access Key ID" : (lang === "fr" ? "Clé API" : "API key")}
                </label>
                <input
                  type="password"
                  value={form[step.id].key}
                  onChange={(e) => setForm((p) => ({ ...p, [step.id]: { ...p[step.id], key: e.target.value } }))}
                  placeholder={svcInfo.placeholder}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm font-mono"
                  data-testid={`byok-input-key-${step.id}`}
                />
              </div>

              {step.id === "ovh_storage" && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-zinc-700 mb-1">OVH Secret Access Key</label>
                    <input
                      type="password"
                      value={form.ovh_storage.secret_key}
                      onChange={(e) => setForm((p) => ({ ...p, ovh_storage: { ...p.ovh_storage, secret_key: e.target.value } }))}
                      className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm font-mono"
                      data-testid="byok-input-ovh-secret"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-zinc-700 mb-1">
                      {lang === "fr" ? "Nom du bucket (région BHS Beauharnois Québec)" : "Bucket name (BHS Beauharnois Québec region)"}
                    </label>
                    <input
                      type="text"
                      value={form.ovh_storage.bucket_name}
                      onChange={(e) => setForm((p) => ({ ...p, ovh_storage: { ...p.ovh_storage, bucket_name: e.target.value } }))}
                      placeholder="mon-bucket-knoria"
                      className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm font-mono"
                      data-testid="byok-input-ovh-bucket"
                    />
                    <p className="text-[10px] text-zinc-500 mt-1">
                      ⚠️ {lang === "fr"
                        ? "Le bucket DOIT être créé en région BHS (Québec) pour la conformité Loi 25."
                        : "Bucket MUST be in BHS (Québec) region for Loi 25 compliance."}
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {step.id === "done" && (
            <div className="space-y-4 text-center" data-testid="byok-step-done">
              <div className="text-6xl">🎉</div>
              <h3 className="text-lg font-semibold text-zinc-900">
                {lang === "fr" ? "Forfait AI configuré !" : "AI Package configured!"}
              </h3>
              <p className="text-sm text-zinc-700">
                {status?.complete
                  ? (lang === "fr"
                      ? "Toutes les fonctionnalités IA sont activées. Vous pouvez uploader votre première vidéo."
                      : "All AI features are enabled. You can now upload your first video.")
                  : (lang === "fr"
                      ? `Étapes terminées (${status?.configured_count || 0}/${status?.total_count || 3}). Vous pouvez finir la configuration plus tard depuis Paramètres → Forfait AI.`
                      : `Steps completed (${status?.configured_count || 0}/${status?.total_count || 3}). You can finish setup later from Settings → AI Package.`)}
              </p>
              <button
                onClick={() => { close(true); navigate("/dashboard/upload"); }}
                className="mt-2 px-6 py-2.5 bg-[#002FA7] hover:bg-[#001F7A] text-white text-sm font-semibold rounded-lg"
                data-testid="byok-step-done-upload-btn"
              >
                🎥 {lang === "fr" ? "Uploader ma première vidéo" : "Upload my first video"}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-between gap-2 flex-wrap">
          <button
            onClick={goToFullSettings}
            className="text-xs text-zinc-500 hover:text-zinc-700 underline"
            data-testid="byok-onboarding-full-settings-btn"
          >
            {lang === "fr" ? "Aller à la page complète →" : "Open full settings page →"}
          </button>
          <div className="flex items-center gap-2">
            {isService && (
              <button
                onClick={skipStep}
                disabled={saving}
                className="px-3 py-2 text-xs rounded border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-700 disabled:opacity-50"
                data-testid="byok-onboarding-skip-btn"
              >
                {lang === "fr" ? "Passer cette étape" : "Skip this step"}
              </button>
            )}
            <button
              onClick={nextStep}
              disabled={saving}
              className="px-5 py-2 text-xs rounded-lg bg-[#002FA7] hover:bg-[#001F7A] text-white font-semibold disabled:opacity-50"
              data-testid="byok-onboarding-next-btn"
            >
              {saving
                ? (lang === "fr" ? "Validation..." : "Validating...")
                : step.id === "welcome"
                  ? (lang === "fr" ? "C'est parti →" : "Let's go →")
                  : isLast
                    ? (lang === "fr" ? "Fermer" : "Close")
                    : isService
                      ? (lang === "fr" ? "Tester et continuer →" : "Test and continue →")
                      : (lang === "fr" ? "Suivant →" : "Next →")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
